import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { constructMSAImageUrl } from 'helpers';
import { HEART_DEFAULT_LIST_ID } from 'constants/apis';
import { INFLUENCER_COLLECTION } from 'constants/influencerPages';
import useMartyContext from 'hooks/useMartyContext';
import useEvent from 'hooks/useEvent';
import { convertRichTextToPlainText } from 'helpers/HtmlHelpers';

import css from 'styles/components/influencer/collections/influencerCollectionListCard.scss';

const MAX_ICON_IMAGES = 4;
const MSA_SINGLE_IMAGE_CONTAINER_DIMENSION = { width: 400, height: 400 };
const MSA_MULTIPLE_IMAGE_CONTAINER_DIMENSION = { width: 138, height: 152 };

export const makeCollectionIcons = items => {
  if (items?.length) {
    let imagesToMake;
    let imageDimension;
    let imageContainerClass;

    const showSingleImage = items.length < MAX_ICON_IMAGES ? true : false;
    if (showSingleImage) {
      imagesToMake = 1;
      imageDimension = MSA_SINGLE_IMAGE_CONTAINER_DIMENSION;
      imageContainerClass = css.singleImageContainer;
    } else {
      imagesToMake = MAX_ICON_IMAGES;
      imageDimension = MSA_MULTIPLE_IMAGE_CONTAINER_DIMENSION;
      imageContainerClass = css.groupedImagesContainer;
    }

    const images = items
      .slice(0, imagesToMake)
      .map(({ imageId }) => <img key={imageId} className={css.productImage} src={constructMSAImageUrl(imageId, imageDimension)} alt="" />);
    return <div className={imageContainerClass}>{images}</div>;
  } else {
    return;
  }
};

export const InfluencerCollectionListCard = ({
  listId,
  items,
  name,
  description,
  onOpenShareModal,
  onOpenDeleteModal,
  hideCardMenu = false,
  showShare = true
}) => {
  const cardMenu = useRef();
  const [isCardMenuOpen, setCardMenu] = useState(false);
  const { testId } = useMartyContext();

  useEvent(window, 'keydown', e => {
    if (isCardMenuOpen && (e.key === 'Escape' || e.key === 'Esc')) {
      setCardMenu(false);
    }
  });

  const bindCloseEvent = e => {
    if (!e.target.closest(`[data-list="${listId}"]`)) {
      setCardMenu(false);
      document.body.removeEventListener('click', bindCloseEvent);
    }
  };

  const onToggleCardMenu = () => {
    setCardMenu(!isCardMenuOpen);
    isCardMenuOpen && cardMenu.current?.focus();
    document.body.addEventListener('click', bindCloseEvent);
  };

  return (
    <article className={css.collectionListCard} data-test-id={testId('collectionCard')}>
      <Link className={css.cardContent} aria-hidden="true" tabIndex="-1" to={`${INFLUENCER_COLLECTION}${listId}`}>
        <p className={css.info}>
          <span className={css.name} title={name} data-test-id={testId('collectionName')}>
            {name}
          </span>
          <span className={css.description} title={convertRichTextToPlainText(description)}>
            {convertRichTextToPlainText(description)}
          </span>
        </p>
        {items && makeCollectionIcons(items)}
      </Link>
      <div className={css.details}>
        <div className={css.menuContainer}>
          {!hideCardMenu && (
            <div data-list={listId}>
              <button
                type="button"
                className={css.cardMenuButton}
                onClick={onToggleCardMenu}
                aria-expanded={isCardMenuOpen}
                aria-haspopup="menu"
                aria-label={`${isCardMenuOpen ? 'Close' : 'Open'} ${name} collection menu`}
                data-test-id={testId('cardMenuButton')}
              />
              {isCardMenuOpen && (
                <div ref={cardMenu} className={css.cardMenu}>
                  {listId !== HEART_DEFAULT_LIST_ID && ( // hide the delete button from default list (api limitation)
                    <button type="button" data-listid={listId} onClick={onOpenDeleteModal} data-test-id={testId('deleteCollection')}>
                      Delete
                    </button>
                  )}
                  {showShare && items.length > 0 && (
                    <button type="button" data-listid={listId} onClick={onOpenShareModal} data-test-id={testId('shareCollection')}>
                      Share Collection
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default InfluencerCollectionListCard;
